import type { ChatMessageEntry } from '../../UIState';

/**
 * Shared scroll decisions for the desktop game-chat and the mobile drawer.
 *
 * Both decide in willUpdate, before Lit adds the new line, and act in
 * updated(). Deciding before is what makes "were you at the bottom?" a fair
 * question: measured after, a line tall enough (one that wraps) reads as
 * having scrolled you away. And both key on the newest line's id, not the
 * count: the 50-line cap removes the oldest as each new one arrives, so a full
 * history never changes length.
 */

/** A line in view while reading back, and where it sat. */
export interface ReadingAnchor {
  line: Element;
  top: number;
}

/** Lines that bring the view to the newest line even while reading back, as
 *  legacy did: anything you sent (whisper-to carries the recipient, so it's
 *  matched by type), system replies (isFromMe), and whispers to you. */
export function jumpsToLatest(entry: ChatMessageEntry): boolean {
  return entry.isFromMe || entry.type === 'whisper-to' || entry.type === 'whisper-from';
}

/** The line at the bottom edge of the view and where it sits. Any line that
 *  stays mounted works as the anchor; the bottom one isn't the oldest, which
 *  the 50-line cap removes as a new one arrives. */
export function captureReadingAnchor(container: HTMLElement): ReadingAnchor | null {
  const viewBottom = container.getBoundingClientRect().bottom;
  const lines = container.children;
  for (let i = lines.length - 1; i >= 0; i--) {
    const line = lines[i];
    if (!line) continue;
    const top = line.getBoundingClientRect().top;
    if (top < viewBottom) return { line, top };
  }
  return null;
}

/** Lines were added below the anchor and maybe removed above it: shifts the
 *  view by however far the anchored line moved. */
export function restoreReadingAnchor(container: HTMLElement, anchor: ReadingAnchor): void {
  if (anchor.line.isConnected) container.scrollTop += anchor.line.getBoundingClientRect().top - anchor.top;
}
